import type { Editor } from 'tinymce'
import {
  TINYMCE_CASE_FIELD_SCHEMA,
  TINYMCE_INVALID_ELEMENTS,
  caseFieldEditorContent,
} from '@messenger/utils'

export { TINYMCE_CASE_FIELD_SCHEMA }

/**
 * The schema every TinyMCE box editor runs under: any element and attribute,
 * since box HTML legitimately carries `<style>` elements, tables, arbitrary
 * inline styles and `data-*` markers. TinyMCE 5 matches every element
 * against the `*[*]` wildcard even when `invalid_elements` names it, so the
 * box editors refuse the active elements through `refuseActiveElements`
 * instead of through this schema.
 */
export const TINYMCE_BOX_SCHEMA = {
  valid_elements: '*[*]',
  extended_valid_elements: 'span[*]',
}

/**
 * Has a case-field editor (one running `TINYMCE_CASE_FIELD_SCHEMA`) load a
 * stored XAML obituary as HTML (`caseFieldEditorContent`), with its
 * paragraphs and bold and italic, instead of letting the schema flatten it on
 * the next save. It runs on every value the editor sets, and an editor on any
 * other schema is left alone.
 */
export const loadCaseFieldXamlAsHtml = ({
  editor,
}: {
  editor: Editor
}): void => {
  editor.on('BeforeSetContent', (event) => {
    if (
      editor.getParam('valid_elements') !==
      TINYMCE_CASE_FIELD_SCHEMA.valid_elements
    )
      return
    if (typeof event.content === 'string')
      event.content = caseFieldEditorContent(event.content)
  })
}

/**
 * Handler attributes a browser runs that no prototype names as a property:
 * SVG's animation events, and handlers older engines still honour.
 */
const UNLISTED_EVENT_HANDLER_ATTRIBUTES = [
  'onbegin',
  'onend',
  'onrepeat',
  'onactivate',
  'onfocusin',
  'onfocusout',
  'onzoom',
]

/**
 * Every event handler attribute this browser knows (`onerror`, `onload`,
 * `onpageshow`, …), read from every element's, the document's and the
 * window's prototype, with the ones no prototype names. TinyMCE's parser
 * filters attributes by exact name only, and its box schema keeps every
 * attribute.
 */
const eventHandlerAttributes = (): string[] => {
  const names = new Set(UNLISTED_EVENT_HANDLER_ATTRIBUTES)
  const prototypes: object[] = [Document.prototype, Window.prototype, window]
  for (const global of Object.getOwnPropertyNames(window)) {
    if (!global.endsWith('Element')) continue
    const constructor = (window as unknown as Record<string, unknown>)[global]
    if (typeof constructor === 'function' && constructor.prototype)
      prototypes.push(constructor.prototype)
  }
  for (const prototype of prototypes)
    for (const name of Object.getOwnPropertyNames(prototype))
      if (name.startsWith('on')) names.add(name.toLowerCase())
  return [...names]
}

/**
 * Form wrappers whose text a person sees, so only the element goes. A
 * textarea is not one: TinyMCE's parser reads its content as raw text, so
 * unwrapping it would turn that text into markup.
 */
const UNWRAPPED_ACTIVE_ELEMENTS = new Set(['form', 'button'])

/**
 * Has the editor's parser refuse the active elements
 * (`TINYMCE_INVALID_ELEMENTS`) whatever its schema: script, embedded
 * documents, page-wide elements, form controls and SVG animation elements
 * go with their content; a form or a button goes and its text stays. Every
 * event handler attribute this browser knows (`eventHandlerAttributes`) goes
 * from every element the parser builds. It runs on every value the editor
 * parses (`setContent`, a paste, an inserted template).
 * This is the editor's belt; the shared sanitizers are what make stored
 * HTML safe.
 */
export const refuseActiveElements = ({ editor }: { editor: Editor }): void => {
  const names = TINYMCE_INVALID_ELEMENTS.split(',')
  const removed = names.filter((name) => !UNWRAPPED_ACTIVE_ELEMENTS.has(name))
  const unwrapped = names.filter((name) => UNWRAPPED_ACTIVE_ELEMENTS.has(name))
  // The parser exists from PreInit, before the editor parses its first value.
  editor.on('PreInit', () => {
    editor.parser.addNodeFilter(removed.join(','), (nodes) => {
      for (const node of nodes) node.remove()
    })
    // TinyMCE's parser reads some markup that the shared sanitizers read as
    // inert, such as `</1 <img src=x onerror=…>`, as an element with a
    // handler; this drops every handler attribute this browser knows
    // (`eventHandlerAttributes`) on an element TinyMCE's parser builds. It
    // does not reach markup that parser keeps as a comment or as raw text and
    // the browser later reads as an element; the shared sanitizers remain the
    // defence for stored HTML.
    const handlers = eventHandlerAttributes()
    editor.parser.addAttributeFilter(handlers.join(','), (nodes, name) => {
      for (const node of nodes) node.attr(name, null)
    })
    editor.parser.addNodeFilter(unwrapped.join(','), (nodes) => {
      for (const node of nodes) node.unwrap()
    })
  })
}
